import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createEquipmentType,
  deleteEquipmentType,
  deleteFailureTypes,
  deleteInspectionPoints,
  deleteRepairActions,
  getEquipmentTypes,
  updateEquipmentType,
} from '@/services/hvl/equipment-type'
import type { IEquipmentTypeFilters } from '@/types/hvl/filters'
import type { IBulkIDsDeleteForm, IEquipmentTypeUpsertForm } from '@/types/hvl/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useEquipmentType = () => {
  const client = useQueryClient()

  const getEquipmentTypeQuery = (filters: IEquipmentTypeFilters) =>
    useQuery({
      queryKey: ['equipment-types', filters],
      queryFn: () => getEquipmentTypes(filters),
    })

  const createEquipmentTypeMutation = useMutation({
    mutationFn: (form: IEquipmentTypeUpsertForm) => createEquipmentType(form),
    onError: NotifyErrorWrapper('Failed to create equipment type'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment-types'] })
      NotifySuccess(`Equipment type created successfully`)
    },
  })

  const updateEquipmentTypeMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: IEquipmentTypeUpsertForm }) => updateEquipmentType(id, form),
    onError: NotifyErrorWrapper('Failed to update equipment type'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment-types'] })
      NotifySuccess(`Equipment type updated successfully`)
    },
  })

  const deleteEquipmentTypeMutation = useMutation({
    mutationFn: (id: number) => deleteEquipmentType(id),
    onError: NotifyErrorWrapper('Failed to delete equipment type'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment-types'] })
      NotifySuccess(`Equipment type deleted successfully`)
    },
  })

  const deleteInspectionPointsMutation = useMutation({
    mutationFn: ({ equipmentTypeID, form }: { equipmentTypeID: number; form: IBulkIDsDeleteForm }) =>
      deleteInspectionPoints(equipmentTypeID, form),
    onError: NotifyErrorWrapper('Failed to delete inspection point'),
  })

  const deleteFailureTypesMutation = useMutation({
    mutationFn: ({ equipmentTypeID, form }: { equipmentTypeID: number; form: IBulkIDsDeleteForm }) =>
      deleteFailureTypes(equipmentTypeID, form),
    onError: NotifyErrorWrapper('Failed to delete failure types'),
  })

  const deleteRepairActionsMutation = useMutation({
    mutationFn: ({ equipmentTypeID, form }: { equipmentTypeID: number; form: IBulkIDsDeleteForm }) =>
      deleteRepairActions(equipmentTypeID, form),
    onError: NotifyErrorWrapper('Failed to delete repair actions'),
  })

  return {
    getEquipmentTypeQuery,
    createEquipmentTypeMutation,
    updateEquipmentTypeMutation,
    deleteEquipmentTypeMutation,
    deleteInspectionPointsMutation,
    deleteFailureTypesMutation,
    deleteRepairActionsMutation,
  }
}
